import NextImage from 'next/image';
import { EventTypesEnum } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import { formatInVenueZone } from 'utils/shared/time/formatInVenueZone';
import { formatVenueTimeRange } from 'utils/shared/time/formatVenueTimeRange';
import Directions from 'svg/Directions';
import License from 'svg/License';
import Location from 'svg/Location';
import Users from 'svg/Users';
import type { FindGame } from 'screens/Find/types';
import ActivityBadge from 'components/ActivityBadge';
import AvatarStack from 'components/AvatarStack';
import Button, { ButtonLink } from 'components/Button';
import DateColumn from 'components/DateColumn';
import Dot from 'components/Dot';
import GamePlayers from 'components/Find/GamePlayers';
import { GameFormat } from 'components/Find/gameFormat';
import Link from 'components/Link';
import { OpportunityItem, TopOpportunityEvent } from './types';

interface Props {
  item: OpportunityItem;
  onGameClick: (game: FindGame) => void;
}

const ICON_CLASS =
  'h-4 w-4 shrink-0 text-color-text-lightmode-icon max-lg:h-3.5 max-lg:w-3.5 dark:text-color-text-darkmode-icon';

const weekdayOf = (start: string, timezone?: string) => formatInVenueZone(start, 'EEE', timezone);

const directionsUrl = (query: string) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;

function DirectionsLink({ destination }: { destination: string }) {
  return (
    <a
      href={directionsUrl(destination)}
      target="_blank"
      rel="noopener noreferrer"
      className="typography-product-button-label-small flex shrink-0 items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-lg:hidden"
    >
      <Directions className="h-3.5 w-3.5 shrink-0" />
      Directions
    </a>
  );
}

function GameSpots({ game }: { game: FindGame }) {
  // Open Play is one roster whose capacity is `playersPerTeam`; the others are two sides.
  const totalSeats = game.playersPerTeam * (game.format === GameFormat.OpenPlay ? 1 : 2);
  const filledSeats = game.teams.flat().filter(Boolean).length;
  const spotsLeft = Math.max(totalSeats - filledSeats, 0);
  if (spotsLeft === 0) return null;
  return (
    <span className="typography-product-text-card-desktop whitespace-nowrap text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
      {pluralize({ count: spotsLeft, singular: 'spot' })} left
    </span>
  );
}

function EventSpots({ event }: { event: TopOpportunityEvent }) {
  if (!event.participantLimit) return null;
  const spotsLeft = Math.max(event.participantLimit - event.registrationCount, 0);
  if (spotsLeft === 0) return null;
  return (
    <span className="typography-product-text-card-desktop whitespace-nowrap text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
      {pluralize({ count: spotsLeft, singular: 'spot' })} left
    </span>
  );
}

export default function OpportunityRow({ item, onGameClick }: Props) {
  const isGame = item.kind === 'game';
  const game = isGame ? item.game : null;
  const event = !isGame ? item.event : null;

  const timezone = event?.timezone ?? game?.timezone;
  // Rendered twice — inline with the badge on desktop, on its own line on mobile.
  const time = formatVenueTimeRange(
    game ? game.startDateTime : event!.startDateTime,
    game ? game.endDateTime : event!.endDateTime,
    timezone,
  );
  const venueName = game ? game.venueName : event?.venueName || event?.address;
  const organizerName = game
    ? game.teams.flat().find((player) => player?.isHost)?.name
    : event?.organizerName;

  return (
    <div className="relative flex items-center gap-ds-lg px-ds-xl max-lg:items-start max-lg:gap-ds-sm max-lg:px-0">
      <DateColumn
        date={item.sortDate}
        timezone={timezone}
        widthClassName="sm:w-[8.75rem]"
        mobileVariant="rail"
      />

      {/* Mobile lays the card out as [media | info]; `lg:contents` dissolves the
          wrapper so the desktop row stays one flat flex line. */}
      <div className="flex min-w-0 flex-1 gap-ds-sm lg:contents">
        {/* Media slot: games show the roster box, events their cover image. */}
        {game ? (
          <>
            <div className="h-[7.5rem] w-[7.5rem] shrink-0 lg:hidden">
              <GamePlayers
                teams={game.teams}
                playersPerTeam={game.playersPerTeam}
                format={game.format}
                compact
              />
            </div>
            <div className="w-[21.25rem] shrink-0 max-lg:hidden">
              <GamePlayers
                teams={game.teams}
                playersPerTeam={game.playersPerTeam}
                format={game.format}
              />
            </div>
          </>
        ) : (
          event && (
            <div className="relative h-[12.5rem] w-[21.25rem] shrink-0 overflow-hidden rounded-xl border border-color-border-input-lightmode dark:border-color-border-input-darkmode max-lg:h-[7.5rem] max-lg:w-[7.5rem] max-lg:rounded-lg">
              <NextImage
                src={event.coverImageUrl}
                alt={event.title}
                fill
                className="object-cover object-center"
                loading="lazy"
              />
            </div>
          )
        )}

        <div className="flex min-w-0 flex-1 flex-col justify-between gap-ds-md self-stretch py-ds-xs max-lg:justify-start max-lg:gap-ds-xs max-lg:py-0">
          <div className="flex flex-col gap-ds-sm max-lg:gap-ds-xs">
            <div className="flex items-center justify-between gap-ds-sm">
              <div className="flex min-w-0 items-center gap-ds-lg">
                <p className="typography-product-element-label flex items-center gap-ds-xs whitespace-nowrap text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-lg:hidden">
                  <span>{weekdayOf(item.sortDate, timezone)}</span>
                  <Dot />
                  <span>{time}</span>
                </p>
                {game ? (
                  <ActivityBadge
                    type={EventTypesEnum.OpenPlay}
                    sport={game.sport}
                    competitiveness={game.competitiveness}
                    size="xs"
                    sizeDesktop="sm"
                  />
                ) : (
                  event && (
                    <ActivityBadge
                      type={event.type}
                      sport={event.sport}
                      isRfx={event.isRfx}
                      size="xs"
                      sizeDesktop="sm"
                    />
                  )
                )}
              </div>
              {(game?.priceFrom !== undefined || event?.priceDisplay) && (
                <span className="flex shrink-0 items-baseline gap-ds-xs whitespace-nowrap">
                  <span className="typography-product-text-card-desktop text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder max-lg:hidden">
                    from
                  </span>
                  <span className="max-lg:typography-product-card-title-mobile lg:typography-product-card-title-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {game ? `$${game.priceFrom}` : event?.priceDisplay}
                  </span>
                </span>
              )}
            </div>

            <h3 className="max-lg:typography-product-card-title-mobile lg:typography-product-card-title-desktop line-clamp-1 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {game ? game.title : event?.title}
            </h3>

            {/* Mobile moves the time out of the badge row onto its own line. */}
            <p className="typography-product-chips-filters text-color-text-lightmode-primary dark:text-color-text-darkmode-primary lg:hidden">
              {time}
            </p>

            {event?.description && (
              <p className="typography-product-text-card-desktop line-clamp-2 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-lg:hidden">
                {event.description}
              </p>
            )}

            <div className="flex flex-col gap-ds-xs max-lg:gap-0">
              {venueName && (
                <div className="flex items-center gap-ds-md max-lg:gap-ds-xs">
                  <p className="max-lg:typography-product-text-card-mobile lg:typography-product-text-card-desktop flex min-w-0 items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    <Location className={ICON_CLASS} />
                    <span className="truncate">{venueName}</span>
                  </p>
                  {game?.distanceMiles != null && (
                    <span className="typography-product-text-card-mobile flex shrink-0 items-center gap-ds-xs whitespace-nowrap text-color-text-lightmode-primary dark:text-color-text-darkmode-primary lg:hidden">
                      <Dot />
                      {Math.round(game.distanceMiles)}mi
                    </span>
                  )}
                  <DirectionsLink
                    destination={game ? game.venueName : event?.address || venueName}
                  />
                </div>
              )}
              {organizerName && (
                <p className="max-lg:typography-product-text-card-mobile lg:typography-product-text-card-desktop flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  <License className={ICON_CLASS} />
                  <span className="truncate">{organizerName}</span>
                </p>
              )}
              {/* Mobile carries the headcount here instead of in the (hidden) footer. */}
              {event && event.registrationCount > 0 && (
                <p className="typography-product-text-card-mobile flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:hidden">
                  <Users className={ICON_CLASS} />
                  <span className="truncate">
                    {pluralize({ count: event.registrationCount, singular: 'player' })}
                  </span>
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-ds-md max-lg:hidden">
            <div className="flex min-w-0 items-center gap-ds-md">
              {event && event.avatarUrls.length > 0 && (
                <>
                  <p className="typography-product-text-card-desktop flex items-center gap-ds-sm whitespace-nowrap text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    <span>Players going</span>
                    <span>
                      {event.registrationCount}
                      {event.participantLimit ? `/${event.participantLimit}` : ''}
                    </span>
                  </p>
                  <AvatarStack urls={event.avatarUrls} sizeClassName="size-6" />
                </>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-ds-md">
              {game ? <GameSpots game={game} /> : event && <EventSpots event={event} />}
              {game ? (
                <Button variant="primary" size="md" onClick={() => onGameClick(game)}>
                  Join the game
                </Button>
              ) : (
                event && (
                  <ButtonLink
                    href={event.href}
                    variant="primary"
                    size="md"
                    className="min-w-[8.75rem]"
                  >
                    Register
                  </ButtonLink>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* The mobile card has no CTA by design, so the row itself is the tap target. */}
      {game ? (
        <button
          type="button"
          aria-label={`Open ${game.title}`}
          onClick={() => onGameClick(game)}
          className="absolute inset-0 lg:hidden"
        />
      ) : (
        event && (
          <Link
            href={event.href}
            aria-label={`Open ${event.title}`}
            className="absolute inset-0 lg:hidden"
          />
        )
      )}
    </div>
  );
}
